import React, { useEffect } from "react"
import styled from "styled-components"
import { ExternalLink, ArrowLeft } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"

const DetailPageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom right, #f0f7ff, #e6eeff);
  font-family: 'Nunito', 'Segoe UI', sans-serif;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const DetailCard = styled.div`
  /* background-color: white; */
  border-radius: 0.75rem;
  /* box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1); */
  max-width: 1000px;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    flex-direction: row;
    min-height: 400px;
  }
`

const CardImage = styled.div`
  flex: 1;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  min-height: 250px;
  
  @media (min-width: 768px) {
    min-height: auto;
    min-width: 40%;
  }
`

const CardContent = styled.div`
  padding: 2rem;
  flex: 1.5;
  display: flex;
  flex-direction: column;
`

const CardTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`

const CardDescription = styled.p`
  font-size: 1.125rem;
  color: #64748b;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  flex-grow: 1;
`

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
`

const FeatureItem = styled.li`
  font-size: 1rem;
  color: #334155;
  display: flex;
  align-items: center;
  
  &:before {
    content: "•";
    color: #4f46e5;
    font-size: 1.5rem;
    margin-right: 0.5rem;
  }
`

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const MetaItem = styled.div`
  background-color: #f8fafc;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  font-size: 0.875rem;
  color: #64748b;
  border: 1px solid #e2e8f0;
`

const LinkButton = styled.a`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: white;
  background-color: #4f46e5;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  align-self: flex-start;
  width: 90%;
  
  &:hover {
    background-color: #4338ca;
    transform: translateY(-2px);
  }
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  color: #334155;
  border: none;
  padding: 0.75rem 0;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1.5rem;

  &:hover {
    color: #4f46e5;
  }
`

const HeaderContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  margin-bottom: 1.5rem;
`

export default function ResourceDetail() {
  const location = useLocation()
  const navigate = useNavigate()
  const resource = location.state?.resource

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate('/resources')
  }

  if (!resource) {
    return (
      <DetailPageContainer>
        <HeaderContainer>
          <BackButton onClick={handleBack}>
            <ArrowLeft size={20} />
            Back to Resources
          </BackButton>
        </HeaderContainer>
        <div style={{ padding: '2rem', backgroundColor: 'white', borderRadius: '0.75rem' }}>
          <h2>Resource not found</h2>
          <p>The resource you're looking for could not be found. Please return to the resources page.</p>
        </div>
      </DetailPageContainer>
    )
  }

  return (
    <DetailPageContainer>
      <HeaderContainer>
        <BackButton onClick={handleBack}>
          <ArrowLeft size={20} />
          Back to Resources
        </BackButton>
      </HeaderContainer>
      
      <DetailCard>
        <CardImage src={resource.image} />
        <CardContent>
          <CardTitle>{resource.title}</CardTitle>
          
          <MetaInfo>
            {resource.category && <MetaItem>{resource.category}</MetaItem>}
            {resource.type && <MetaItem>{resource.type}</MetaItem>}
            {resource.difficulty && <MetaItem>Level: {resource.difficulty}</MetaItem>}
            {resource.duration && <MetaItem>Duration: {resource.duration}</MetaItem>}
          </MetaInfo>
          
          <CardDescription>{resource.fullDescription}</CardDescription>
          
          {resource.features && resource.features.length > 0 && (
            <>
              <h3>Key Features:</h3>
              <FeaturesList>
                {resource.features.map((feature, index) => (
                  <FeatureItem key={index}>{feature}</FeatureItem>
                ))}
              </FeaturesList>
            </>
          )}
          
          <LinkButton href={resource.link} target="_blank" rel="noopener noreferrer">
            Learn More <ExternalLink size={16} />
          </LinkButton>
        </CardContent>
      </DetailCard>
    </DetailPageContainer>
  )
}
